import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Badge } from "@/marketing/components/ui/badge";
import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/marketing/components/ui/table";
import type { Brand } from "@/marketing/models/brand";
import { brandAPIClient } from "@/marketing/infrastructure/influencer/brand/brandAPIClient";
import { BRAND_KEYS } from "@/marketing/infrastructure/influencer/brand/utils/keys";
import { BRAND_QUERIES } from "@/marketing/infrastructure/influencer/brand/utils/queries";

import { BrandFormDialog } from "./BrandFormDialog";
import { LinkListCell } from "./LinkListCell";

const PAGE_LIMIT = 20;
const COLUMN_COUNT = 8;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function SkeletonRow() {
  return (
    <TableRow>
      {Array.from({ length: COLUMN_COUNT }).map((_, i) => (
        <TableCell key={i}>
          <div className="tw-h-4 tw-animate-pulse tw-rounded tw-bg-muted" />
        </TableCell>
      ))}
    </TableRow>
  );
}

interface DeleteConfirmDialogProps {
  brand: Brand | null;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function DeleteConfirmDialog({
  brand,
  onConfirm,
  onCancel,
  isPending,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog open={brand !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="tw-max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete brand?</DialogTitle>
          <p className="tw-text-sm tw-text-muted-foreground">
            This will soft-delete{" "}
            <span className="tw-font-semibold">{brand?.name}</span>. This action
            can be reversed by an admin.
          </p>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BrandManagerPage() {
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [deletingBrand, setDeletingBrand] = useState<Brand | null>(null);

  const apiFilter = { limit: PAGE_LIMIT, offset };

  const {
    data: brands = [],
    isLoading,
    isError,
    refetch,
  } = useQuery(BRAND_QUERIES.list(apiFilter));

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return brands;
    return brands.filter(
      (b) =>
        b.name.toLowerCase().includes(term) ||
        (b.slug ?? "").toLowerCase().includes(term),
    );
  }, [brands, search]);

  const deleteMutation = useMutation({
    mutationFn: (brandId: string) => brandAPIClient.deleteBrand(brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRAND_KEYS.lists() });
      setDeletingBrand(null);
      toast.success("Brand deleted");
    },
    onError: (error) => {
      const axiosError = error as AxiosError<{ message?: string }>;
      const msg = axiosError?.response?.data?.message ?? "Failed to delete brand";
      toast.error(msg);
    },
  });

  const from = offset + 1;
  const to = offset + brands.length;
  const hasPrev = offset > 0;
  const hasNext = brands.length === PAGE_LIMIT;

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-overflow-hidden tw-p-6">
      {/* Page header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div>
          <h1 className="tw-text-2xl tw-font-semibold tw-leading-tight">
            Brand Manager
          </h1>
          <p className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
            Manage brands used across influencer campaigns
          </p>
        </div>
        <Button
          size="sm"
          className="tw-flex-shrink-0 tw-gap-1.5"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="tw-h-3.5 tw-w-3.5" />
          New Brand
        </Button>
      </div>

      {/* Search */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or slug…"
          className="tw-h-9 tw-w-[280px] tw-text-sm"
        />
      </div>

      {/* Error state */}
      {isError && (
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-destructive/40 tw-bg-destructive/10 tw-px-4 tw-py-3 tw-text-sm tw-text-destructive">
          Failed to load brands.
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-rounded-md tw-border tw-border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw-w-12">Logo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Brand Assets</TableHead>
              <TableHead>Product Links</TableHead>
              <TableHead>Website</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="tw-w-24 tw-text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="tw-py-16 tw-text-center tw-text-sm tw-text-muted-foreground"
                >
                  {search
                    ? `No brands matching "${search}"`
                    : "No brands yet."}
                  {!search && (
                    <div className="tw-mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreateOpen(true)}
                      >
                        <Plus className="tw-mr-1.5 tw-h-3.5 tw-w-3.5" />
                        New Brand
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((brand) => (
                <TableRow key={brand.brandId}>
                  <TableCell>
                    {brand.logoUrl ? (
                      <img
                        src={brand.logoUrl}
                        alt={brand.name}
                        className="tw-h-8 tw-w-8 tw-rounded-md tw-object-contain"
                      />
                    ) : (
                      <div className="tw-h-8 tw-w-8 tw-rounded-md tw-bg-muted" />
                    )}
                  </TableCell>
                  <TableCell className="tw-font-medium">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {brand.name}
                      {brand.organizationId && (
                        <Badge
                          variant="secondary"
                          className="tw-px-2 tw-py-0 tw-text-[10px] tw-font-medium"
                          title={`Business Portal organization ${brand.organizationId}`}
                        >
                          Portal linked
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="tw-font-mono tw-text-xs tw-text-muted-foreground">
                    {brand.slug ?? "—"}
                  </TableCell>
                  <TableCell>
                    <LinkListCell links={brand.brandAssetsLinks} />
                  </TableCell>
                  <TableCell>
                    <LinkListCell links={brand.productLinks} />
                  </TableCell>
                  <TableCell>
                    {brand.websiteUrl ? (
                      <a
                        href={brand.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-primary hover:tw-underline"
                      >
                        <ExternalLink className="tw-h-3 tw-w-3" />
                        Visit
                      </a>
                    ) : (
                      <span className="tw-text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="tw-text-sm tw-text-muted-foreground">
                    {formatDate(brand.updatedAt)}
                  </TableCell>
                  <TableCell className="tw-text-right">
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="tw-h-7 tw-w-7"
                        onClick={() => setEditingBrand(brand)}
                        title="Edit brand"
                      >
                        <Pencil className="tw-h-3.5 tw-w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
                        onClick={() => setDeletingBrand(brand)}
                        title="Delete brand"
                      >
                        <Trash2 className="tw-h-3.5 tw-w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!isLoading && brands.length > 0 && (
        <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-muted-foreground">
          <span>
            Showing {from}–{to}
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasNext}
              onClick={() => setOffset(offset + PAGE_LIMIT)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <BrandFormDialog
        open={createOpen || !!editingBrand}
        mode={editingBrand ? "edit" : "create"}
        initialBrand={editingBrand ?? undefined}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditingBrand(null);
          }
        }}
      />

      {/* Delete confirm dialog */}
      <DeleteConfirmDialog
        brand={deletingBrand}
        onConfirm={() => {
          if (deletingBrand) deleteMutation.mutate(deletingBrand.brandId);
        }}
        onCancel={() => setDeletingBrand(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
